import { useCallback } from 'react';
import { apiClient } from '@services/api';
import { useActiveOrgStore, useAuthStore } from '@store/index';
import { applyLocale } from '../i18n/localePreference';
import type { SupportedLanguage } from '../i18n';

/**
 * REV-798: change the language and, for a signed-in person, remember the choice.
 *
 * Returns a function taking the chosen language, or null for "automatic" (no
 * stored preference). It switches the UI first, so the person sees the result
 * at once, then saves to the API (PUT /v1/users/me) and updates the auth store,
 * which useLocalePreference reads: that is what carries the choice across
 * pages, reloads and the next sign-in with no re-login.
 *
 * Signed out there is nothing to save, so it only switches the UI.
 *
 * Switching first is deliberate, as in AppearanceTab: a failed save rejects, for
 * the caller to report, rather than reverting a language the person is already
 * reading. The rejection is the raw error; render it with toDisplayError.
 *
 * Clearing the preference (null) falls back to the organization's, then the
 * browser's, matching the order the API resolves text in.
 */
export function useSaveLocalePreference(): (locale: SupportedLanguage | null) => Promise<void> {
  const signedIn = useAuthStore((s) => s.isAuthenticated);
  const setUser = useAuthStore((s) => s.setUser);

  return useCallback(
    async (locale) => {
      await applyLocale(locale ?? useActiveOrgStore.getState().activeOrg?.locale ?? null);
      if (!signedIn) return;
      const res = await apiClient.put<{ success: boolean; data: { locale?: string | null } }>(
        '/v1/users/me',
        { locale }
      );
      // The store may have changed while the request was in flight (sign-out,
      // a profile edit); merge into whatever it holds now.
      const current = useAuthStore.getState().user;
      if (current) setUser({ ...current, locale: res.data.locale ?? null });
    },
    [signedIn, setUser]
  );
}
